<template>
  <div>
    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :label="label"
      :tooltipText="tooltipText"
      :errorText="errorText"
      :helpText="helpText"
      :disclaimer="disclaimer">
      <template v-slot:input >
        <div class="d-flex">
          <div>
            <select
              :class="{'is-invalid': v$.tin && v$.tin.value.$error }"
              class="form-select me-5"
              @blur="onBlur(v$.tin.value)"
              @change="onTINChange()"
              v-model="tin.value"
              data-cy="tin"
            >
              <option value="null" disabled selected>Select</option>
              <option value="ein">EIN</option>
              <option value="ssn">SSN</option>
            </select>
          </div>
          <div class="flex-grow-1">
            <div id="borrowerAttributeSsn" v-if="tin.value === 'ssn'">
              <input :id="'ba' + taxIds.ssn.id" :name="'ba' + taxIds.ssn.id" class="form-control"
                data-cy="ssn"
                type="password"
                :class="{'is-invalid': v$.taxIds.ssn.value.$invalid && v$.taxIds.ssn.value.$dirty}"
                v-model="taxIds.ssn.value"
                v-maska data-maska="#########"
                masked=true
                :disabled="disabled"
                :required="v$.taxIds.ssn.required"
                placeholder="Social Security Number"
                :maxlength="9"
                :minlength="9"
                @keyup="onChange(v$.taxIds.ssn.value, $event, 'ssn')"
                @blur="onBlur(v$.taxIds.ssn.value)"
              />
            </div>

            <div id="borrowerAttributeTaxId" v-if="tin.value === 'ein'">
              <input :id="'ba' + taxIds.federalTaxId.id" :name="'ba' + taxIds.federalTaxId.id" class="form-control"
                  :class="{'is-invalid': v$.taxIds.federalTaxId.value.$invalid && v$.taxIds.federalTaxId.value.$dirty}"
                data-cy="ein"
                type="text"
						    inputmode="numeric"
						    pattern="[0-9]*"
                v-model="taxIds.federalTaxId.value"
                v-maska data-maska="#########"
                masked=true
                :disabled="disabled"
                :required="v$.taxIds.federalTaxId.required"
                placeholder="EIN"
                :maxlength="9"
                :minlength="6"
                @keyup="onChange(v$.taxIds.federalTaxId.value, $event, 'federalTaxId')"
                @blur="onBlur(v$.taxIds.federalTaxId.value)"
              />
            </div>

            <!-- No selection -->
            <input class="form-control"
              type="text"
              disabled
              v-if="!tin.value"
              :class="{'is-invalid': v$.taxIds && v$.taxIds.federalTaxId.value.$error && v$.taxIds.ssn.value.$error }"
            />
          </div>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'
import get from 'lodash/get'
import BorrowerAttributeConstructor from './BorrowerAttributeConstructor.vue'

export default {
  components: {
    BorrowerAttributeConstructor,
  },
  emits: ['invalidity','answer'],
  setup: () => ({ v$: useVuelidate() }),
  data () {
    return {
      tin: this.tinType(),
      ssnErrorMessage: 'Please enter a 9 digit Social Security number',
      taxIdErrorMessage: 'Please enter a 9 digit Employer ID number',
      generalErrorMessage: 'Select either EIN or SSN',
      taxIds: this.formatTaxIds(),
    }
  },
  created () {
    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})
  },

  validations: function () {
    return {
      tin: {
        value: {
          required
        }
      },
      taxIds: {
        federalTaxId: {
          value: this.EINValidation,
        },
        ssn: {
          value: this.SSSNValidation
        }
      }
    }
  },

  computed: {
    invalid() {
      return this.v$.$invalid
    },
    EINValidation () {
      if (this.tin.value === 'ein') {
        return {
          required,
          minLength: minLength(9)
        }
      }
      if (this.tin.value === null) { // Require this question in general if no option is selected
        return {
          required
        }
      }
      return {
        // No requirements
      }
    },
    SSSNValidation () {
      if (this.tin.value === 'ssn') {
        return {
          required,
          minLength: minLength(9)
        }
      }
      if (this.tin.value === null) { // Require this question in general if no option is selected
        return {
          required
        }
      }
      return {
        // No requirements
      }
    },
    // element computed fields
    questionText() {
      return this.question.questionText || ''
    },
    description() {
      return this.question.description || ''
    },
    descriptionTooltip() {
      return this.question.descriptionTooltip || ''
    },
    label() {
      return this.question.name || ''
    },
    tooltipText() {
      return this.question.tooltip || this.question.helpText || ''
    },
    helpText() {
      return this.question.tooltip ? this.question.helpText : ''
    },
    errorText() {
      if (get(this.v$, 'tin.value.$error')) {
        return this.generalErrorMessage
      } else {
        return this.inputError
      }
    },
    disclaimer() {
      return this.question.disclaimer || ''
    },
    inputError() {
      if (get(this.v$, 'taxIds.ssn.value.$error')) {
        return this.ssnErrorMessage
      }
      if (get(this.v$, 'taxIds.federalTaxId.value.$error')) {
        return this.taxIdErrorMessage
      }
      return ''
    },
  },

  methods: {
    onTINChange () {
      if (!this.taxIds.ssn.value || this.taxIds.ssn.value.length < 11) {
        this.taxIds.ssn.value = null
      }
      if (!this.taxIds.federalTaxId.value || this.taxIds.federalTaxId.value.length < 9) {
        this.taxIds.federalTaxId.value = null
      }
      this.v$.$reset()
    },

    tinType () {
      let _federalTaxId = this.question.components.find(function (c) { return c.alias === 'federalstate_tax_id' })
      let _ssn = this.question.components.find(function (c) { return c.alias === 'contact.ssn' })

      if (_federalTaxId.value) {
        return { value: 'ein' }
      }

      if (_ssn.value) {
        return { value: 'ssn' }
      }

      return { value: null }
    },

    formatTaxIds () {
      function getMinimalAttributes ({ value, id, alias } = {}) {
        return {
          alias,
          value: value || null,
          attributeId: id
        }
      }
      let _federalTaxId = this.question.components.find(function (c) { return c.alias === 'federalstate_tax_id' })
      let _ssn = this.question.components.find(function (c) { return c.alias === 'contact.ssn' })

      let _values = {
        federalTaxId: getMinimalAttributes(_federalTaxId),
        ssn: getMinimalAttributes(_ssn),
      }

      return cloneDeep(_values)
    },
    onChange (v$, e, model) {
      let question = {
        alias: this.taxIds[model].alias,
        value: this.taxIds[model].value || null
      }

      this.$emit('invalidity', v$.$invalid)
      this.$emit('answer', { value: question, isInvalid: v$.$invalid })

      if (!v$.$invalid) {
        v$.$touch() // If the attribute gets brought into a valid state, and then an invalid state, it should display errored.
      }
    },
    onBlur (v$) {
      v$.$touch()
    }
  },

  props: {
    question: Object,
    disabled: Boolean
  }
}
</script>

<style lang="scss" scoped>

.no-select {
  color: $neutral-500;
}
</style>
